// Legal/policy page content — GenieX. Central source referenced by PolicyPage.jsx.
// Edit copy here; layout stays in the shared PolicyPage component.

export const POLICY_CONTENT = {
  "privacy-policy": {
    title: "Privacy Policy",
    updated: "21 August 2026",
    sections: [
      {
        heading: "1. Overview",
        body: [
          "GenieX (\"we\", \"us\", \"our\") designs, sells and installs smart home automation products and services in India. This Privacy Policy explains what information we collect through geniex.in and our shop, how we use it, and the choices you have.",
        ],
      },
      {
        heading: "2. Information we collect",
        body: [
          "Account & sign-in: when you sign in with Google to shop or view your orders, we receive your name, email address and profile photo from Google.",
          "Order & shipping details: full name, phone number, delivery address and order contents, so we can fulfil and ship your order.",
          "Consultation requests: name, phone/email and project details you submit through our contact or consultation forms.",
          "Payments: payments are processed directly by PayU, our payment gateway partner. GenieX never receives or stores your card, UPI or bank account details.",
          "Usage data: basic technical data (browser, device type, pages visited) to keep the site secure and improve performance.",
        ],
      },
      {
        heading: "3. How we use your information",
        body: [
          "To process and deliver orders, share shipping/tracking updates, and provide customer support.",
          "To respond to consultation and installation requests.",
          "To send order-related emails (confirmation, shipped, delivered) and, if you opt in, occasional product updates.",
          "To detect fraud, secure our systems, and comply with legal obligations.",
        ],
      },
      {
        heading: "4. Sharing your information",
        body: [
          "We share data only with the service providers needed to run GenieX: Google (sign-in), PayU (payments), our shipping/logistics partners, and our email provider (transactional order emails). We do not sell your personal data to third parties.",
        ],
      },
      {
        heading: "5. Data retention & security",
        body: [
          "We retain order and account data for as long as needed to fulfil orders, meet tax/accounting requirements, and resolve disputes. We use industry-standard safeguards (encrypted connections, access controls) to protect your data.",
        ],
      },
      {
        heading: "6. Your rights",
        body: [
          "You may request access to, correction of, or deletion of your personal data by writing to hello@geniex.in. We will respond within a reasonable time, subject to our legal and record-keeping obligations.",
        ],
      },
      {
        heading: "7. Contact us",
        body: [
          "GenieX, 3rd Floor, KNR Square, Door No. 1-60, opp. The Platina, Gachibowli, Hyderabad, Telangana 500032. Email: hello@geniex.in · Phone: +91 90636 62999.",
        ],
      },
    ],
  },

  "terms-of-service": {
    title: "Terms of Service",
    updated: "21 August 2026",
    sections: [
      {
        heading: "1. Acceptance of terms",
        body: [
          "By accessing geniex.in or purchasing from the GenieX shop, you agree to these Terms of Service. If you do not agree, please do not use the site.",
        ],
      },
      {
        heading: "2. Products & pricing",
        body: [
          "We make reasonable efforts to display accurate product information, images and pricing. Prices are in Indian Rupees (INR) and inclusive of applicable taxes unless stated otherwise. We reserve the right to correct pricing errors and to modify or discontinue products at any time.",
        ],
      },
      {
        heading: "3. Orders & payment",
        body: [
          "Placing an order is an offer to purchase, which we may accept or decline (e.g. for stock or payment issues). Payments are processed securely by PayU. An order is confirmed only once payment is successfully verified.",
        ],
      },
      {
        heading: "4. Shipping & installation",
        body: [
          "Shipping timelines and fees are described in our Shipping & Installation Policy. Where installation services are booked separately (e.g. via a consultation), scheduling is coordinated directly with our installation team.",
        ],
      },
      {
        heading: "5. Cancellations, returns & warranty",
        body: [
          "Cancellations and refunds are governed by our Refund Policy. Product warranty coverage is governed by our Warranty Policy.",
        ],
      },
      {
        heading: "6. Account & user conduct",
        body: [
          "You are responsible for keeping your Google-authenticated account secure. You agree not to misuse the site (e.g. attempting unauthorized access, scraping, or fraudulent orders).",
        ],
      },
      {
        heading: "7. Intellectual property",
        body: [
          "All content on geniex.in — including text, graphics, logos and product designs — is owned by or licensed to GenieX and may not be copied or reused without written permission.",
        ],
      },
      {
        heading: "8. Limitation of liability",
        body: [
          "GenieX is not liable for indirect or consequential damages arising from use of our products or website, to the maximum extent permitted by Indian law. Nothing in these terms limits any liability that cannot be excluded under applicable law.",
        ],
      },
      {
        heading: "9. Governing law",
        body: [
          "These terms are governed by the laws of India, with courts in Hyderabad, Telangana having exclusive jurisdiction.",
        ],
      },
      {
        heading: "10. Contact us",
        body: ["Questions about these terms? Email hello@geniex.in or call +91 90636 62999."],
      },
    ],
  },

  "refund-policy": {
    title: "Refund & Cancellation Policy",
    updated: "21 August 2026",
    sections: [
      {
        heading: "1. Order cancellation",
        body: [
          "You may cancel an order free of charge any time before it has been shipped. Once an order has shipped, it cannot be cancelled — please refer to our returns process below instead.",
        ],
      },
      {
        heading: "2. Returns",
        body: [
          "Unopened, unused products in original packaging may be returned within 7 days of delivery. To start a return, email hello@geniex.in with your order number and reason for return.",
          "Products that show signs of use, missing accessories/packaging, or custom-configured items are not eligible for return unless defective.",
        ],
      },
      {
        heading: "3. Damaged or defective items",
        body: [
          "If a product arrives damaged or defective, contact us within 48 hours of delivery with photos and your order number. We will arrange a free replacement or full refund.",
        ],
      },
      {
        heading: "4. Refund timelines",
        body: [
          "Approved refunds are processed to your original payment method via PayU within 5–7 business days of approval. Bank processing times may add a few additional days.",
        ],
      },
      {
        heading: "5. Installation services",
        body: [
          "Consultation and installation service bookings may be rescheduled or cancelled up to 24 hours before the scheduled visit at no charge. Cancellations within 24 hours may be subject to a nominal visit fee, communicated at the time of booking.",
        ],
      },
      {
        heading: "6. Contact us",
        body: ["For any cancellation or refund request, email hello@geniex.in or call +91 90636 62999."],
      },
    ],
  },

  "shipping-policy": {
    title: "Shipping & Installation Policy",
    updated: "21 August 2026",
    sections: [
      {
        heading: "1. Shipping coverage",
        body: ["We ship pan-India through trusted logistics partners. Delivery timelines vary by location and product availability."],
      },
      {
        heading: "2. Shipping fees",
        body: [
          "A flat shipping fee applies per order, shown at checkout before payment. Orders above our free-shipping threshold ship free. Certain bulky or specialised products may carry a different shipping fee, which is always shown clearly at checkout before you pay — never hidden.",
        ],
      },
      {
        heading: "3. Delivery timelines",
        body: [
          "Most in-stock orders are dispatched within 1–2 business days and delivered within 5–9 business days, depending on your location. You will receive an email once your order ships, and a tracking number where available.",
        ],
      },
      {
        heading: "4. Delays",
        body: [
          "Occasionally, deliveries may be delayed due to courier disruptions, weather, or events beyond our control (force majeure). We will keep you informed if this affects your order.",
        ],
      },
      {
        heading: "5. Installation scheduling",
        body: [
          "For products that require professional installation, our team will contact you after delivery (or after your consultation booking) to schedule a convenient installation slot.",
        ],
      },
      {
        heading: "6. Contact us",
        body: ["Shipping questions? Email hello@geniex.in or call +91 90636 62999."],
      },
    ],
  },

  "warranty-policy": {
    title: "Warranty Policy",
    updated: "21 August 2026",
    sections: [
      {
        heading: "1. Standard warranty coverage",
        body: [
          "Unless stated otherwise on the product page, GenieX devices carry a 1-year manufacturer warranty from the date of delivery, covering manufacturing defects in materials and workmanship.",
        ],
      },
      {
        heading: "2. What's covered",
        body: [
          "Hardware failures under normal residential use, such as a switch, sensor or controller that stops functioning without any external damage.",
        ],
      },
      {
        heading: "3. What's not covered",
        body: [
          "Physical damage, water/fire damage, unauthorized repairs or modifications, damage from incorrect voltage/wiring, normal wear and tear, and issues caused by third-party software or networks.",
        ],
      },
      {
        heading: "4. How to make a warranty claim",
        body: [
          "Email hello@geniex.in with your order number, product name and a description (and photos/video, if possible) of the issue. Our support team will guide you through diagnosis, and arrange a repair or replacement if the issue is covered.",
        ],
      },
      {
        heading: "5. Repair vs. replacement",
        body: [
          "At our discretion, we will repair the product, replace it with the same or an equivalent model, or issue a prorated refund if neither is possible.",
        ],
      },
      {
        heading: "6. Contact us",
        body: ["Warranty questions? Email hello@geniex.in or call +91 90636 62999."],
      },
    ],
  },

  "cookie-policy": {
    title: "Cookie Policy",
    updated: "21 August 2026",
    sections: [
      {
        heading: "1. What are cookies",
        body: [
          "Cookies are small text files stored on your device that help websites function and remember information between visits.",
        ],
      },
      {
        heading: "2. How GenieX uses cookies & similar storage",
        body: [
          "Essential: a secure session cookie keeps you signed in with Google across the shop and account pages.",
          "Cart: your shopping cart is stored in your browser's local storage so items persist between visits — this stays on your device and is not a tracking cookie.",
          "Analytics (optional): anonymised usage data may be collected to help us understand which pages are useful and improve the site.",
        ],
      },
      {
        heading: "3. Third-party cookies",
        body: [
          "Signing in with Google, and paying via PayU, may set their own cookies governed by Google's and PayU's respective privacy policies.",
        ],
      },
      {
        heading: "4. Managing cookies",
        body: [
          "Most browsers let you block or delete cookies via their settings. Blocking essential cookies may prevent sign-in or checkout from working correctly.",
        ],
      },
      {
        heading: "5. Contact us",
        body: ["Questions about cookies? Email hello@geniex.in."],
      },
    ],
  },
};
